import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { ToastType } from 'react-toastify';

import type {
  Campaign,
  ECampaignObjective,
  ICampaignAttributes,
  TCampaignGroup,
} from '@feathr/blackbox';
import { CAMPAIGN_MAX_BUDGET_INTERNAL, REGEX_INPUT_VALIDATION } from '@feathr/blackbox';
import {
  CampaignClass,
  CampaignLabelMap,
  getMinDaysAdvance,
  isTTDCampaignWithObjective,
  leadTimeCampaigns,
} from '@feathr/blackbox';
import type { IModalProps } from '@feathr/components';
import {
  AlertV2 as Alert,
  Button,
  EAlertV2Type,
  Input,
  Modal,
  Skeleton,
  toast,
  Well,
} from '@feathr/components';
import type { TModalSize } from '@feathr/components/src/Modal/Modal';
import CampaignTypeChip from '@feathr/extender/components/CampaignTypeChip';
import ProjectSelect from '@feathr/extender/components/ProjectSelect';
import { useCampaignPermissions } from '@feathr/extender/hooks';
import useGoogleAdsCustomer from '@feathr/extender/hooks/useGoogleAdsCustomer';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';
import { logUserEvents } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import AddCampaignModalPartnerSelect from './AddCampaignModalPartnerSelect';
import AddCampaignModalPartnerTLDR from './AddCampaignModalPartnerTLDR';
import CampaignTypeSelect from './CampaignTypeSelect';
import ObjectiveStep from './ObjectiveStep';
import PixlPlusUpsell from './PixlPlusUpsell/PixlPlusUpsell';

import styles from './AddCampaignModal.css';

// Exported validation function for testing
export function validateCampaignName(name: string, t: TFunction): string | undefined {
  const error: Array<string | undefined> = validate.single(name, {
    presence: {
      allowEmpty: false,
      message: t("Can't be blank"),
    },
    format: {
      pattern: REGEX_INPUT_VALIDATION,
      flags: 'i',
      message: t('Campaign names must consist of letters, numbers, and basic symbols.'),
    },
  });
  return error ? error[0] : undefined;
}

export interface IAddCampaignModalProps extends Pick<IModalProps, 'onClose' | 'opened'> {
  campaignClasses?: CampaignClass[];
  campaignGroup?: TCampaignGroup;
  flightId?: string;
  partnerId?: string;
  /** Callback function for additional processing once a campaign is created. */
  postSave?: (campaign: Campaign) => Promise<void> | void;
  redirect?: boolean;
}

interface IModalText {
  title: string;
  description: string;
}

function getModalText(t: TFunction, campaignGroup: TCampaignGroup): IModalText {
  const campaignGroupTextMap: Record<TCampaignGroup, IModalText> = {
    ads: {
      title: t('Create an ad'),
      description: t("Choose the type of ad you'd like to create."),
    },
    all: {
      title: t('Create a campaign'),
      description: t("Choose the type of campaign you'd like to create."),
    },
    email: {
      title: t('Create an email'),
      description: t("Choose the type of email you'd like to create."),
    },
    'google-ads': {
      title: t('Create a Google Ad Grants campaign'),
      description: t("Choose the type of Google Ads campaign you'd like to create."),
    },
    monetization: {
      title: t('Create a monetization campaign'),
      description: t("Choose the type of monetization campaign you'd like to create."),
    },
    other: {
      title: t('Create a campaign'),
      description: t("Choose the type of campaign you'd like to create."),
    },
  };
  return campaignGroupTextMap[campaignGroup];
}

type TAddCampaignModalStep = 'select' | 'name' | 'upsell' | 'objective';

function AddCampaignModal({
  campaignClasses,
  campaignGroup,
  flightId,
  onClose,
  partnerId: defaultPartnerId,
  postSave,
  redirect = true,
  opened,
}: Readonly<IAddCampaignModalProps>): JSX.Element {
  const account = useAccount();
  const { Campaigns, Events } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { eventId: projectId } = useParams<{ eventId?: string }>();
  const [campaignType, setCampaignType] = useState<CampaignClass | undefined>();
  const [name, setName] = useState<string | undefined>();
  const [isMonetization, setIsMonetization] = useState<boolean>(campaignGroup === 'monetization');
  const [partnerId, setPartnerId] = useState<string | undefined>(defaultPartnerId);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [selectedObjective, setSelectedObjective] = useState<ECampaignObjective | undefined>(
    undefined,
  );
  const { t } = useTranslation();
  const googleAdsCustomer = useGoogleAdsCustomer();
  const googleAdsCustomerId = googleAdsCustomer?.id;
  const [modalStep, setModalStep] = useState<TAddCampaignModalStep>('select');
  const [originalCampaignType, setOriginalCampaignType] = useState<CampaignClass | undefined>(
    undefined,
  );
  const { omni: showPixlPlus } = useCampaignPermissions();

  // Helper functions for step progression
  const hasUpsellStep = (campaignClass: CampaignClass): boolean => {
    return (
      showPixlPlus &&
      (campaignClass === CampaignClass.Segment || campaignClass === CampaignClass.EmailList)
    );
  };

  const hasObjectiveStep = (campaignClass: CampaignClass): boolean => {
    return isTTDCampaignWithObjective(campaignClass);
  };

  const getTotalSteps = (campaignClass: CampaignClass): number => {
    let steps = 2;
    if (hasUpsellStep(campaignClass)) {
      steps++;
    }
    if (hasObjectiveStep(campaignClass)) {
      steps++;
    }
    return steps;
  };

  const getCurrentStepNumber = (): number => {
    if (!campaignType) {
      return 1;
    }

    switch (modalStep) {
      case 'select':
        return 1;

      case 'upsell':
        return 2;

      case 'name':
        return hasUpsellStep(campaignType) ? 3 : 2;

      case 'objective':
        return getTotalSteps(campaignType);

      default:
        return 1;
    }
  };

  const getCurrentProjectName = useCallback((): ReactNode => {
    // Use the projectId from the URL if available, otherwise use the selectedProjectId
    const currentProjectId = projectId ?? selectedProjectId;
    if (!currentProjectId) {
      return undefined;
    }

    const project = Events.get(currentProjectId);

    if (project.isPending) {
      return <Skeleton paragraphs={1} title={false} />;
    }

    return project.name;
  }, [selectedProjectId]);

  // Update partnerId state when partnerId prop is changed.
  useEffect(() => {
    setPartnerId(defaultPartnerId);
  }, [defaultPartnerId]);

  function handleTypeChange(newCampaignType: CampaignClass, isMonetization = false): void {
    setCampaignType(newCampaignType);
    setIsMonetization(isMonetization);

    if (hasUpsellStep(newCampaignType)) {
      setModalStep('upsell');
      setOriginalCampaignType(newCampaignType);
    } else {
      setModalStep('name');
    }
  }

  function reset(): void {
    setCampaignType(undefined);
    setIsMonetization(false);
    setName(undefined);
    setPartnerId(defaultPartnerId);
    setSelectedObjective(undefined);
    setOriginalCampaignType(undefined);
    setModalStep('select');
  }

  function handlePreviousClick(): void {
    if (!campaignType) {
      return;
    }

    switch (modalStep) {
      case 'upsell':
        setModalStep('select');
        reset();
        break;

      case 'name':
        if (hasUpsellStep(campaignType)) {
          setModalStep('upsell');
        } else {
          setModalStep('select');
          reset();
        }
        break;

      case 'objective':
        setModalStep('name');
        break;
    }
  }

  function handleNextFromName(): void {
    if (!campaignType) {
      return;
    }

    if (hasObjectiveStep(campaignType)) {
      setModalStep('objective');
    } else {
      handleCreateCampaign();
    }
  }

  async function handleClose(): Promise<void> {
    await onClose();
    // Prevent data from persisting once modal is closed.
    reset();
  }

  // During this modal stage, the campaign object is not yet created, so we can't use its validate method.
  function validateCampaignNameInternal(): string | undefined {
    return validateCampaignName(name ?? '', t);
  }

  // During this modal stage, the campaign object is not yet created, so we can't use its validate method.
  function validateCampaignProject(): string | undefined {
    if (projectId) {
      return;
    }
    const error: Array<string | undefined> = validate.single(selectedProjectId, {
      presence: {
        allowEmpty: false,
        message: t('^Please select a project.'),
      },
    });
    return error ? error[0] : undefined;
  }

  async function handleCreateCampaign(): Promise<void> {
    if (
      !campaignType ||
      !name ||
      !!validateCampaignNameInternal() ||
      (!partnerId && isMonetization) ||
      (isMonetization && !account.hasMonetization) ||
      (hasObjectiveStep(campaignType) && !selectedObjective)
    ) {
      // This should never happen.
      throw new Error(
        t('Campaign type, name, and objective are required to create a new campaign!'),
      );
    }

    let overrides: Partial<ICampaignAttributes> = {
      name,
    };

    // Add objective for TTD campaigns
    if (hasObjectiveStep(campaignType) && selectedObjective) {
      overrides.objective = selectedObjective;
    }

    // If it's a monetization campaign, set monetization-specific attributes.
    if (partnerId) {
      overrides = {
        ...overrides,
        parent: partnerId,
        parent_kind: 'partner',
        bidding_strategy: {
          cross_device_vendor_id: 0,
        },
        exposure_settings: {
          target_type: 'fixed_impressions',
          // If the account is internal, set to 25 (the max budget for playground campaigns) to
          // prevent the modal from failing to create the campaign due to budget restrictions.
          target_value: account.get('is_internal') ? CAMPAIGN_MAX_BUDGET_INTERNAL : 1000,
        },
      };
    }
    if (flightId) {
      overrides = {
        ...overrides,
        flight: flightId,
      };
    }

    if (googleAdsCustomerId && campaignType === CampaignClass.GoogleAdsSmart) {
      overrides.customer = googleAdsCustomerId;
      overrides.advertising_language_code = 'en';
    }

    const response = await Campaigns.addCampaign(
      campaignType,
      /*
       * If there is no projectId, we are in the account level context where the user must select a project
       * before creating a campaign. We validate that a project was selected on lines 269-271, so we can
       * assert that selectedProjectId is defined here.
       */
      projectId ?? selectedProjectId!,
      overrides,
    );
    if (postSave) {
      await postSave(response);
    }

    if (!response.isPending && !response.isErrored) {
      toast(t('Draft campaign created.'), { type: ToastType.SUCCESS });
      const userEventDetails = {
        account_id: account.id,
        campaign_id: response.id,
        campaign_name: response.name,
      };
      logUserEvents({
        'Created campaign': userEventDetails,
        [`Created ${CampaignLabelMap.get(response.get('_cls'))} campaign`]: userEventDetails,
      });
      await onClose();

      if (redirect) {
        if (campaignType === CampaignClass.LandingPage) {
          history.push(localUrl(response.getItemUrl()));
        } else {
          history.push(localUrl(response.getItemUrl('edit')));
        }
      }
    } else {
      toast(t('Unable to create campaign. Try again or contact support if the issue persists.'), {
        type: ToastType.ERROR,
      });
    }
  }

  const handleTryPixlPlus = (): void => {
    setCampaignType(CampaignClass.Omni);
    setModalStep('name');
  };

  const handleContinueWithSelection = (): void => {
    setCampaignType(originalCampaignType);
    setModalStep('name');
  };

  const backButton = (
    <Button key={'back'} onClick={handlePreviousClick}>
      {t('Back')}
    </Button>
  );

  const nameStepActions = [
    backButton,
    <Button
      disabled={
        !!validateCampaignNameInternal() ||
        !!validateCampaignProject() ||
        (isMonetization && !partnerId)
      }
      key={campaignType && hasObjectiveStep(campaignType) ? 'next' : 'create'}
      onClick={handleNextFromName}
      type={'primary'}
    >
      {campaignType && hasObjectiveStep(campaignType) ? t('Next') : t('Create')}
    </Button>,
  ];

  const objectiveStepActions = [
    backButton,
    <Button
      disabled={!selectedObjective}
      key={'create'}
      onClick={handleCreateCampaign}
      type={'primary'}
    >
      {t('Create')}
    </Button>,
  ];

  const upsellStepActions = [
    backButton,
    <Button key={'next'} onClick={handleTryPixlPlus} type={'primary'}>
      {t('Try Pixl Plus')}
    </Button>,
  ];

  const stepMap = {
    select: {
      title: (campaignGroup && getModalText(t, campaignGroup).title) ?? t('Create a campaign'),
      description:
        (campaignGroup && getModalText(t, campaignGroup).description) ??
        t("Choose the type of campaign you'd like to create."),
      rightActions: undefined,
      size: 'lg',
      leftActions: undefined,
    },
    upsell: {
      title: t('Try Pixl Plus'),
      description: t(
        'Our new Pixl Plus campaign provides enhanced performance compared to standard {{type}}.',
        {
          type: CampaignLabelMap.get(originalCampaignType ?? CampaignClass.Segment),
        },
      ),
      rightActions: upsellStepActions,
      size: 'default',
      leftActions: campaignType
        ? t('Step {{current}} of {{total}}', {
            current: 2,
            total: getTotalSteps(campaignType),
          })
        : undefined,
    },
    name: {
      title: t('Create a campaign'),
      description: t('Name this campaign.'),
      rightActions: nameStepActions,
      size: 'default',
      leftActions: campaignType
        ? t('Step {{current}} of {{total}}', {
            current: getCurrentStepNumber(),
            total: getTotalSteps(campaignType),
          })
        : undefined,
    },
    objective: {
      title: t('Select an objective'),
      description: t('Select the objective you would like to achieve.'),
      rightActions: objectiveStepActions,
      size: 'lg',
      leftActions: campaignType
        ? t('Step {{current}} of {{total}}', {
            current: getCurrentStepNumber(),
            total: getTotalSteps(campaignType),
          })
        : undefined,
    },
  };

  const stepContent = stepMap[modalStep];

  return (
    <Modal
      allowOverflow={modalStep !== 'select'}
      description={stepContent.description}
      leftActions={stepContent.leftActions}
      onClose={handleClose}
      opened={opened}
      rightActions={stepContent.rightActions}
      size={stepContent.size as TModalSize}
      title={stepContent.title}
    >
      <div className={styles.container}>
        {modalStep === 'select' && (
          <CampaignTypeSelect
            campaignClasses={campaignClasses}
            campaignGroup={campaignGroup}
            defaultCategory={
              defaultPartnerId || campaignGroup === 'monetization' ? 'monetization' : 'marketing'
            }
            inFlight={!!flightId}
            onSelect={handleTypeChange}
          />
        )}
        {modalStep === 'upsell' && (
          <PixlPlusUpsell
            handleContinueWithSelection={handleContinueWithSelection}
            isMonetization={isMonetization}
            originalCampaignType={originalCampaignType ?? CampaignClass.Segment}
          />
        )}
        {modalStep === 'name' && (
          <>
            {campaignType && leadTimeCampaigns.includes(campaignType) && (
              <Alert
                className={styles.alert}
                title={t(
                  'This type of campaign must be published at least {{count}} day prior to the intended start date.',
                  {
                    count: getMinDaysAdvance({
                      cls: campaignType,
                    }),
                  },
                )}
                type={EAlertV2Type.info}
              />
            )}
            {campaignType && (
              <Well label={t('Campaign type')}>
                <CampaignTypeChip cls={campaignType} isMonetization={isMonetization} />
              </Well>
            )}
            {!projectId && (
              <ProjectSelect onChangeSingle={setSelectedProjectId} value={selectedProjectId} />
            )}
            {isMonetization &&
              (defaultPartnerId ? (
                <AddCampaignModalPartnerTLDR value={defaultPartnerId} />
              ) : (
                <AddCampaignModalPartnerSelect
                  eventId={projectId ?? selectedProjectId}
                  onChange={setPartnerId}
                />
              ))}
            <Input
              label={t('Name')}
              onChange={setName}
              placeholder={t('Enter a campaign name...')}
              validationError={name ? validateCampaignNameInternal() : undefined}
              value={name}
            />
          </>
        )}
        {modalStep === 'objective' && campaignType && (
          <>
            {campaignType && (
              <Well description={getCurrentProjectName()} label={name}>
                <CampaignTypeChip cls={campaignType} isMonetization={isMonetization} />
              </Well>
            )}
            <ObjectiveStep
              campaignType={campaignType}
              onObjectiveChange={setSelectedObjective}
              selectedObjective={selectedObjective}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

export default observer(AddCampaignModal);
